import React, { useEffect, useState } from 'react';
import { RootState } from '../../../store/store';
import { FSRS, Rating } from '../../../services/scheduler';
import { useSelector } from 'react-redux';
import { Flashcard as FlashcardType } from '../../../types/Flashcard';
import { useHotkeys } from 'react-hotkeys-hook';
import Flashcard from './Flashcard';
import moment, { Moment } from 'moment';
import { PanelType } from '../../../types/Study';
import { CircularProgress, Drawer } from '@mui/material';
import {
  getSuspendedFlashcards,
  unsuspendFlashcard,
} from '../../../services/flashcards';
import { hapticsImpactLight } from '../../../utils/haptics';
import { enqueueSnackbar } from 'notistack';
import parse from 'html-react-parser';
import { BarChart, X } from 'react-feather';
import { useRefreshStudySessionAndContent } from '../../../utils/refreshStudySession';
import useWindowWidth from '../../../hooks/useWindowWidth';
import FlashcardsFeatureTour from './FlashcardsTour';
import { useStudyPhaseSend } from '../../../studyPhase';
import FlashLoader from '../../FlashLoader';
import {
  isLoadingPhase,
  selectStudyPhase,
} from '../../../slices/study/studySlice';
import { useFlashcardResponse } from '../../../hooks/useFlashcardResponse';
import { MINIMUM_START_DATE } from '../../../utils/tourHelpers';
import FlashcardHistory from './FlashcardHistory';
import { useFullscreen } from '../../../hooks/useFullscreen';
import ChatInterface from '../../Chat/ChatInterface';
import { Chat } from '../../../assets/svgs/Chat';

const Flashcards: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const [isFlipped, setIsFlipped] = useState(false);
  const [currentFlashcard, setCurrentFlashcard] =
    useState<FlashcardType | null>(null);
  const [currentFlashcardType, setCurrentFlashcardType] = useState('none');
  const [keybindsDisabled, setKeybindsDisabled] = useState(false);
  const [intervalLabels, setIntervalLabels] = useState({});
  const [flashcardMenuIsOpen, setFlashcardMenuIsOpen] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showSuspendedCards, setShowSuspendedCards] = useState(false);
  const [isLoadingSuspendedCards, setIsLoadingSuspendedCards] = useState(false);
  const [suspendedCards, setSuspendedCards] = useState([]);
  const newFlashcards = useSelector((state: RootState) => state.flashcards.new);
  const dueFlashcards = useSelector((state: RootState) => state.flashcards.due);
  const prevFlashcard = useSelector(
    (state: RootState) => state.pastFlashcards[0]
  );
  const [panelType, setPanelType] = useState<PanelType>(PanelType.library);
  const studyPhase = useSelector(selectStudyPhase);
  const [isSideDrawerOpen, setIsSideDrawerOpen] = useState(false);
  const [sideDrawerType, setSideDrawerType] = useState<'chat' | 'history'>(
    'chat'
  );

  // Flashcard Feature Tour
  const [showFlashcardsTour, setShowFlashcardsTour] = useState(false);
  const [isFirstSession, setIsFirstSession] = useState(false);

  const windowWidth = useWindowWidth();

  const { handleResponse, undoResponse } = useFlashcardResponse({
    currentFlashcard,
    currentFlashcardType,
    intervalLabels,
    setIsFlipped,
    setShowErrorMessage,
  });

  const { sendPhaseEvent } = useStudyPhaseSend();

  const { refreshStudySessionAndContent } = useRefreshStudySessionAndContent();
  const { isFullscreen, openFullscreen, closeFullscreen } = useFullscreen();

  // Onboarding States
  const [flashcardStartTime, setFlashcardStartTime] = useState<Moment | null>(
    null
  );

  const fsrs = new FSRS();

  const isMac = /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);
  const undoHotkey = isMac ? 'meta+z' : 'ctrl+z';

  useHotkeys('space', () => keybinds('space'), { keyup: true });
  useHotkeys('f', () => keybinds('f'), { keyup: true });
  useHotkeys('c', () => keybinds('c'), { keyup: true });
  useHotkeys('escape', () => keybinds('esc'), { keyup: true });
  useHotkeys('h', () => keybinds('h'), { keyup: true });
  useHotkeys(undoHotkey, (event) => {
    event.preventDefault();
    keybinds('z', event);
  });
  useHotkeys('z', (event) => {
    if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
      return;
    }
    keybinds('z', event);
  });

  const keybinds = (key: string, event?: KeyboardEvent) => {
    if (event) {
      event.preventDefault();
    }
    if (key === 'esc') {
      setPanelType(PanelType.explanation);
      setKeybindsDisabled(false);
    }
    if (keybindsDisabled) return;

    if (!isFlipped && key === 'space') {
      setIsFlipped(true);
    }
    if (isFlipped && key === 'space') {
      handleResponse(2);
    }
    if (key === 'f') {
      if (isFullscreen) {
        closeFullscreen();
      } else {
        openFullscreen();
      }
    }
    if (key === 'z') {
      undoResponse();
    }
    if (key === 'h' && currentUser?.role === 'admin') {
      if (isSideDrawerOpen && sideDrawerType === 'history') {
        setIsSideDrawerOpen(false);
      } else {
        setSideDrawerType('history');
        setIsSideDrawerOpen(true);
      }
    }
    if (key === 'c') {
      if (isSideDrawerOpen && sideDrawerType === 'chat') {
        setIsSideDrawerOpen(false);
      } else {
        setSideDrawerType('chat');
        setIsSideDrawerOpen(true);
      }
    }
  };

  useEffect(() => {
    fetchSuspendedFlashcards();
  }, []);

  useEffect(() => {
    if (!isFlipped) {
      setIntervalLabels({});
    } else {
      if (!currentFlashcard) return;
      const initialScheduling = currentFlashcard.scheduling;
      const now = new Date();
      const schedulingOptions = fsrs.repeat(initialScheduling, now);

      // Map UI response indices to Rating enum values
      const ratingMap = [Rating.Again, Rating.Hard, Rating.Good, Rating.Easy];
      const timeDiffs = ['', '', '', ''];

      for (let i = 0; i < ratingMap.length; i++) {
        const rating = ratingMap[i];
        const schedulingOption = schedulingOptions[rating];
        if (schedulingOption) {
          const diff = moment(schedulingOption.card.due).diff(now, 'minutes');
          if (diff < 60) {
            timeDiffs[i] = `${diff}m`;
          } else if (diff < 1440) {
            timeDiffs[i] = `${Math.floor(diff / 60)}h`;
          } else {
            timeDiffs[i] = `${Math.floor(diff / 1440)}d`;
          }
        }
      }

      setIntervalLabels({
        0: timeDiffs[0],
        1: timeDiffs[1],
        2: timeDiffs[2],
        3: timeDiffs[3],
      });
    }
  }, [isFlipped]);

  useEffect(() => {
    const [flashcard, type] = calcCurrentFlashcard();
    setCurrentFlashcard(flashcard as FlashcardType | null);
    setIsFlipped(false);
    setCurrentFlashcardType(type as string);
  }, [newFlashcards, dueFlashcards]);

  useEffect(() => {
    if (!flashcardStartTime) {
      setFlashcardStartTime(moment());
    }
    if (!currentFlashcard || !currentFlashcard.contents.length) {
      setShowErrorMessage(true);
    } else {
      setShowErrorMessage(false);
    }
  }, [currentFlashcard, flashcardStartTime]);

  // Fetch suspended flashcards when the user toggles the suspended cards drawer
  useEffect(() => {
    if (showSuspendedCards) {
      fetchSuspendedFlashcards();
    }
  }, [showSuspendedCards]);

  // Handle Showing Flashcards Tour
  useEffect(() => {
    const flashcardsTourStartDate = MINIMUM_START_DATE;

    // Feature Tour
    if (currentUser?.createdAt) {
      const userCreatedAt = moment.utc(currentUser.createdAt);
      if (userCreatedAt.isAfter(flashcardsTourStartDate)) {
        // Prevent showing tour to existing users
        if (currentUser?.onboardingTips) {
          if (!currentUser.onboardingTips['flashcards-tour']) {
            setTimeout(() => {
              setShowFlashcardsTour(true);
              setIsFirstSession(true);
            }, 250);
          }
        }
      }
    }
  }, [currentUser, windowWidth]);

  useEffect(() => {
    if (newFlashcards.length === 0 && dueFlashcards.length === 0) {
      sendPhaseEvent({ type: 'FINISH_FLASHCARDS' });
    }
  }, [newFlashcards.length, dueFlashcards.length]);

  const calcCurrentFlashcard = () => {
    // If there are no due or new flashcards, return null
    if (dueFlashcards.length === 0 && newFlashcards.length === 0) {
      return [null, 'none'];
      // If there is a due flashcard that was studied today and is now due again, return it
    } else if (
      dueFlashcards.length > 0 &&
      isDueNow(dueFlashcards[0].scheduling.due)
    ) {
      return [dueFlashcards[0], 'due'];
      // If there is a due flashcard that was studied on a previous day, return it
    } else if (
      dueFlashcards.length > 0 &&
      !wasReviewedRecently(dueFlashcards[0].scheduling.lastReview)
    ) {
      return [dueFlashcards[0], 'due'];
      // If there are new flashcards, return the first one
    } else if (newFlashcards.length > 0) {
      return [newFlashcards[0], 'new'];
      // If there are due flashcards, return the first one
    } else if (dueFlashcards.length > 0) {
      return [dueFlashcards[0], 'due'];
    } else {
      return [null, 'none'];
    }
  };

  const isDueNow = (dueDate: Date | string): boolean => {
    // Convert both current time and dueDate to moment objects for comparison
    const now = moment();
    const due = moment(dueDate);
    return now.isSameOrAfter(due);
  };

  const wasReviewedRecently = (lastReview: Date | string): boolean => {
    const reviewTime = moment(lastReview);
    const now = moment();
    // Check if the last review was less than 8 hours ago
    return now.diff(reviewTime, 'hours') < 8;
  };

  const fetchSuspendedFlashcards = async () => {
    setIsLoadingSuspendedCards(true);
    const response = await getSuspendedFlashcards();
    if (response) {
      setSuspendedCards(response);
    }
    setIsLoadingSuspendedCards(false);
  };

  const handleUnsuspendFlashcard = async (flashcardId: string) => {
    await unsuspendFlashcard(flashcardId);
    await refreshStudySessionAndContent();
    fetchSuspendedFlashcards();
    enqueueSnackbar('Flashcard unsuspended.', {
      autoHideDuration: 3000,
    });
  };

  return (
    <>
      {!showErrorMessage && (
        <div
          className={`flashcards-main ${isSideDrawerOpen ? 'with-drawer' : ''}`}
        >
          <Flashcard
            flashcard={currentFlashcard}
            isFlipped={isFlipped}
            flipCard={setIsFlipped}
            handleResponse={handleResponse}
            intervalLabels={intervalLabels}
            flashcardMenuIsOpen={flashcardMenuIsOpen}
            setFlashcardMenuIsOpen={setFlashcardMenuIsOpen}
            prevFlashcard={!!prevFlashcard}
            undoResponse={undoResponse}
            keybindsDisabled={keybindsDisabled}
            setKeybindsDisabled={setKeybindsDisabled}
            panelType={panelType}
            setPanelType={setPanelType}
            fetchSuspendedFlashcards={fetchSuspendedFlashcards}
            setShowSuspendedCards={setShowSuspendedCards}
            suspendedCardCount={suspendedCards.length}
            isFirstSession={isFirstSession}
            setIsCardHistoryDrawerOpen={setIsSideDrawerOpen}
          />
          <Drawer
            className="suspended-cards-drawer"
            anchor="right"
            open={showSuspendedCards}
            onClose={() => setShowSuspendedCards(false)}
          >
            <div className="drawer-container">
              <div className="drawer-container_header">
                <h2>Suspended Cards</h2>
                <button
                  onClick={() => setShowSuspendedCards(false)}
                  className="button button--close"
                >
                  <X />
                </button>
              </div>
              {!isLoadingSuspendedCards && (
                <div className="flashcard-container">
                  {suspendedCards.map((card: FlashcardType, index: number) => (
                    <div key={index} className="suspended-card">
                      <div className="flashcard">
                        {parse(card.contents[0].front)}
                        <button
                          onClick={() => {
                            hapticsImpactLight();
                            handleUnsuspendFlashcard(card.id);
                          }}
                          className="button button--unsuspend"
                        >
                          Unsuspend
                        </button>
                      </div>
                    </div>
                  ))}
                  {suspendedCards.length === 0 && (
                    <div className="no-remaining">
                      No remaining suspended flashcards.
                    </div>
                  )}
                </div>
              )}
              {isLoadingSuspendedCards && (
                <div className="loader-wrapper">
                  <CircularProgress
                    sx={{
                      display: 'block',
                      margin: '1.5rem 0 0 0',
                      height: '1rem !important',
                      width: '1rem !important',
                    }}
                  />
                </div>
              )}
            </div>
          </Drawer>
          {showFlashcardsTour && (
            <FlashcardsFeatureTour isFlipped={isFlipped} />
          )}
          {currentFlashcard && (
            <div className="side-drawer">
              <div className="side-drawer_header">
                <button
                  onClick={() => setSideDrawerType('chat')}
                  className={`button button--tab ${
                    sideDrawerType === 'chat' ? 'button--active' : ''
                  }`}
                >
                  <Chat />
                  <span>AI Chat</span>
                </button>
                {currentUser?.role === 'admin' && (
                  <button
                    onClick={() => setSideDrawerType('history')}
                    className={`button button--tab ${
                      sideDrawerType === 'history' ? 'button--active' : ''
                    }`}
                  >
                    <BarChart />
                    <span>History</span>
                  </button>
                )}
              </div>
              <div className="side-drawer_body">
                {sideDrawerType === 'chat' && (
                  <ChatInterface
                    isStudy={true}
                    flashcard={currentFlashcard}
                    flashcardVariant={currentFlashcard?.contents[0]}
                    placeholder="Ask me about this flashcard..."
                  />
                )}
                {sideDrawerType === 'history' && (
                  <FlashcardHistory
                    flashcardId={currentFlashcard.id}
                    isCardHistoryDrawerOpen={
                      isSideDrawerOpen && sideDrawerType === 'history'
                    }
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {showErrorMessage && (
        <div className="flashcard-error">
          <div>
            <h2>Error retrieving flashcard content.</h2>
            <button className="button" onClick={() => keybinds('space')}>
              Continue
            </button>
            <div className="or">or</div>
            <p>Press Space to continue.</p>
          </div>
        </div>
      )}
      <FlashLoader visible={isLoadingPhase(studyPhase)} />
    </>
  );
};

export default Flashcards;
